import type { ProblemDetails } from '@dru/types';

/**
 * The typed API client.
 *
 * Two things it does that a bare `fetch` wrapper does not:
 *
 *   · holds the access token in MEMORY only. Not localStorage — a token in
 *     localStorage is readable by any script that gets onto the page, and this
 *     one opens a tenant's medical records. The refresh token lives in an
 *     HttpOnly cookie the page cannot read at all.
 *
 *   · refreshes transparently on a 401, and de-duplicates concurrent
 *     refreshes. A dashboard firing six requests at once must not perform six
 *     rotations — with reuse detection on the server, five of them would look
 *     like a stolen token and log the user out.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: ProblemDetails,
  ) {
    super(problem.detail || problem.title);
    this.name = 'ApiError';
  }

  /** Stable identifier, e.g. "validation-failed". Switch on this, never on text. */
  get type(): string {
    return this.problem.type.split('/').pop() ?? 'unknown';
  }

  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.problem.errors ?? []) out[e.field] = e.message;
    return out;
  }

  /**
   * True when the server named at least one field. A form shows those
   * beside the fields and keeps the banner short; without any it shows the
   * detail as the only message it has.
   */
  get hasFieldErrors(): boolean {
    return (this.problem.errors?.length ?? 0) > 0;
  }

  /**
   * What a person should read.
   *
   * The idempotency errors are the API protecting a clinical record from a
   * duplicate. They are correct and they are not the user's problem: the
   * client sends the key, and if one is missing that is a bug in this code,
   * not something to explain at the top of a form. A validation failure with
   * field errors lists them, because "one or more fields are invalid" tells
   * nobody which.
   */
  get userMessage(): string {
    switch (this.type) {
      case 'idempotency-key-required':
        return 'This request could not be sent safely. Please reload the page and try again.';
      case 'idempotent-request-in-flight':
        return 'That request is still being processed. Give it a moment before trying again.';
      case 'idempotency-key-reused':
      case 'idempotency-key-reuse':
        return 'This form was already submitted with different details. Reload the page to start again.';
      case 'validation-failed': {
        const errs = this.problem.errors ?? [];
        if (!errs.length) return this.problem.detail || this.problem.title;
        if (errs.length === 1) return errs[0]!.message;
        return `Please correct ${errs.length} fields: ${errs.map((e) => humanField(e.field)).join(', ')}.`;
      }
      default:
        return this.problem.detail || this.problem.title;
    }
  }
}

/** "contactEmail" → "contact email", "(root)" → "the form". */
function humanField(field: string): string {
  if (!field || field === '(root)') return 'the form';
  return field.split('.').pop()!.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
}

/**
 * A key for one logical operation.
 *
 * Minted once per submit attempt of a FORM, not per request: a retry after a
 * network failure resends the same key, so the server replays the stored
 * response instead of creating a second record. `crypto.randomUUID` needs a
 * secure context; the fallback covers a plain-HTTP development origin.
 */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  const bytes = new Uint8Array(16);
  c?.getRandomValues?.(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
    .replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/, '$1-$2-$3-$4-$5');
}

type Listener = (token: string | null) => void;

const SESSION_MARKER = 'dru.session';

class ApiClient {
  private accessToken: string | null = null;
  /**
   * The plant a company-wide login is viewing, from the header switcher.
   * Sent on reads only; the API narrows the read to it. Null = all plants.
   */
  private viewPlantId: string | null = null;

  setViewPlant(plantId: string | null) { this.viewPlantId = plantId; }
  getViewPlant(): string | null { return this.viewPlantId; }
  private refreshing: Promise<boolean> | null = null;
  private listeners = new Set<Listener>();

  constructor(private baseUrl: string) {}

  setToken(token: string | null) {
    this.accessToken = token;
    // Not the token — a marker that this browser has held a session. The
    // refresh cookie is HttpOnly, so on start the page cannot tell whether it
    // has one; without the marker every first visit to /login fires a refresh
    // that answers 401, which the console reports as a failed resource.
    try {
      if (token) localStorage.setItem(SESSION_MARKER, '1');
      else localStorage.removeItem(SESSION_MARKER);
    } catch { /* storage unavailable: resume will simply try the cookie */ }
    for (const l of this.listeners) l(token);
  }

  getToken(): string | null {
    return this.accessToken;
  }

  /** Notified when the session ends, so the app can route to /login. */
  onTokenChange(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  async request<T>(
    method: string,
    path: string,
    opts: { body?: unknown; idempotencyKey?: string; retryOn401?: boolean; allPlants?: boolean } = {},
  ): Promise<T> {
    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers: {
        ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(this.accessToken ? { authorization: `Bearer ${this.accessToken}` } : {}),
        ...(opts.idempotencyKey ? { 'idempotency-key': opts.idempotencyKey } : {}),
        ...(this.viewPlantId && !opts.allPlants && method.toUpperCase() === 'GET' ? { 'x-view-plant': this.viewPlantId } : {}),
      },
      // The refresh cookie must ride along, and it is HttpOnly on the API
      // origin, so every request is credentialed.
      credentials: 'include',
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });

    if (res.status === 401 && opts.retryOn401 !== false) {
      const refreshed = await this.refresh();
      if (refreshed) {
        // Once. A second 401 after a successful refresh means the session is
        // genuinely gone, and retrying again would loop.
        return this.request<T>(method, path, { ...opts, retryOn401: false });
      }
      this.setToken(null);
    }

    if (!res.ok) {
      const problem = await res.json().catch(() => ({
        type: 'unknown', title: res.statusText, status: res.status,
        detail: 'The request failed.',
      })) as ProblemDetails;
      throw new ApiError(res.status, problem);
    }

    if (res.status === 204) return undefined as T;
    return res.json() as Promise<T>;
  }

  /**
   * Resumes a session from the refresh cookie, on app start.
   *
   * Exists so the bootstrap does not hand-roll its own `fetch` to
   * `/auth/refresh` — which both apps did, and which quietly skipped the
   * de-duplication below. A bootstrap rotation racing a screen's first 401
   * presents the server with the same refresh token twice, and reuse detection
   * reads that as theft and kills the whole family: the worker is signed out
   * by the act of opening the app.
   */
  resumeSession(): Promise<boolean> {
    let held = true;
    try { held = localStorage.getItem(SESSION_MARKER) !== null; } catch { /* try anyway */ }
    if (!held) return Promise.resolve(false);
    return this.refresh();
  }

  /**
   * Rotates the refresh token. Concurrent callers share one in-flight
   * rotation: the server treats a replayed refresh token as theft and revokes
   * the whole family, so six parallel rotations would log the user out.
   */
  private refresh(): Promise<boolean> {
    if (this.refreshing) return this.refreshing;

    this.refreshing = (async () => {
      try {
        const res = await fetch(`${this.baseUrl}/auth/refresh`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: '{}',
        });
        if (!res.ok) return false;
        const data = await res.json() as { accessToken: string };
        this.setToken(data.accessToken);
        return true;
      } catch {
        return false;
      } finally {
        this.refreshing = null;
      }
    })();

    return this.refreshing;
  }

  get<T>(path: string) { return this.request<T>('GET', path); }
  /** A read that ignores the viewed plant: the switcher's own list of plants. */
  getAllPlants<T>(path: string) { return this.request<T>('GET', path, { allPlants: true }); }
  /**
   * Every POST carries an Idempotency-Key.
   *
   * The API refuses a clinical create without one, and which endpoints count
   * as clinical is the server's decision — a screen that guesses wrong shows
   * a red banner about headers to somebody trying to save a form. So the
   * header is always sent. A caller that holds a key for the whole submit
   * (see `useIdempotencyKey`) passes it and retries replay; one that does not
   * gets a fresh key per call, which is what the old convention did anyway.
   */
  post<T>(path: string, body?: unknown, idempotencyKey?: string) {
    return this.request<T>('POST', path, {
      body: body ?? {}, idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
    });
  }
  patch<T>(path: string, body: unknown) { return this.request<T>('PATCH', path, { body }); }
  /**
   * Replaces a whole collection.
   *
   * Distinct from `patch` because some endpoints genuinely mean "this is the
   * complete set" — a role's permissions being the first. The invariants
   * there are properties of the whole set, so an endpoint that accepted a
   * partial change would have to read the rest anyway.
   */
  put<T>(path: string, body: unknown) { return this.request<T>('PUT', path, { body }); }
  /**
   * `body` is optional and unusual, but not wrong here.
   *
   * Several deletes in this product refuse without a written reason — removing
   * a worker's document, retiring a role — and the reason belongs in the
   * request that performs the act rather than in a query string, where it
   * would land in access logs alongside the URL.
   */
  del<T>(path: string, body?: unknown) {
    return this.request<T>('DELETE', path, body === undefined ? {} : { body });
  }
}

export function createApiClient(baseUrl: string): ApiClient {
  return new ApiClient(baseUrl);
}

export type { ApiClient };
